<template>
  <div class="flex flex-col gap-3.5">
    <!-- Live region carries only the step label (and the one-time long-wait note);
         the per-second timer and rotating hints stay outside it. -->
    <p class="sr-only" role="status" aria-live="polite">{{ liveText }}</p>
    <div class="flex items-center gap-3">
      <span class="relative grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-primary text-primary-foreground" aria-hidden="true">
        <span class="absolute inset-0 animate-ping rounded-full bg-primary/40 motion-reduce:hidden"></span>
        <Icon name="lucide:cloud-download" class="relative h-[15px] w-[15px]" />
      </span>
      <span class="text-base font-extrabold tracking-[-0.02em]">{{ t("pages.audioToSubtitle.preparingTitle") }}</span>
      <span class="h-px flex-1 bg-border"></span>
      <span class="font-mono text-[13px] tabular-nums text-subtle-foreground"><span class="sr-only">{{ t('pages.audioToSubtitle.preparingElapsed') }}: </span>{{ elapsedLabel }}</span>
    </div>

    <div class="relative h-1.5 overflow-hidden rounded-full bg-muted rtl:-scale-x-100">
      <div class="absolute inset-y-0 left-0 w-2/5 animate-shimmer rounded-full bg-gradient-to-r from-primary/0 via-primary to-primary/0 motion-reduce:w-full motion-reduce:animate-none motion-reduce:bg-primary/40"></div>
    </div>

    <ol class="flex flex-col gap-2">
      <li v-for="(step, index) in steps" :key="step.key" class="flex items-center gap-2.5 text-[13.5px]">
        <span v-if="index < activeIndex" class="grid h-5 w-5 flex-none place-items-center rounded-full bg-success/15 text-success" aria-hidden="true">
          <Icon name="lucide:check" class="h-3 w-3" />
        </span>
        <span v-else-if="index === activeIndex" class="grid h-5 w-5 flex-none place-items-center" aria-hidden="true">
          <span class="h-2.5 w-2.5 animate-pulse rounded-full bg-primary"></span>
        </span>
        <span v-else class="grid h-5 w-5 flex-none place-items-center" aria-hidden="true">
          <span class="h-2.5 w-2.5 rounded-full border border-subtle-foreground/60"></span>
        </span>
        <span
          :class="index === activeIndex
            ? 'font-semibold text-foreground'
            : index < activeIndex ? 'text-muted-foreground' : 'text-subtle-foreground'"
        >
          {{ step.label }}<span v-if="index === activeIndex" class="animate-pulse">…</span>
        </span>
      </li>
    </ol>

    <p :key="hintIndex" class="min-h-[2.5em] animate-in fade-in text-[13px] text-muted-foreground duration-500 motion-reduce:animate-none">
      <Icon name="lucide:lightbulb" class="me-1 inline h-3.5 w-3.5 -translate-y-px text-primary" aria-hidden="true" />{{ hints[hintIndex] }}
    </p>

    <p v-if="isLongWait" class="flex items-start gap-2 rounded-md border border-info/25 bg-info/10 px-3 py-2.5 text-[13px] leading-normal">
      <Icon name="lucide:info" class="mt-px h-4 w-4 flex-none text-info" aria-hidden="true" />
      <span>{{ t("pages.audioToSubtitle.preparingLongWait") }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { MediaPreparingPhase } from "~/types/media-subtitle";

// Indeterminate waiting state for from-URL jobs while the source media is
// fetched (link → resolve on the platform → download into storage). The
// resolve alone can take minutes, so this shows motion and context instead of
// a fake percentage.

const props = defineProps<{
  phase: MediaPreparingPhase;
  /** Epoch ms the wait started at (button press or job creation). */
  startedAt: number;
}>();

const { t } = useI18n();

const HINT_ROTATE_MS = 5000;
const LONG_WAIT_MS = 45_000;

const steps = computed(() => [
  { key: "link", label: t("pages.audioToSubtitle.preparingStepLink") },
  { key: "resolving", label: t("pages.audioToSubtitle.preparingStepResolve") },
  { key: "downloading", label: t("pages.audioToSubtitle.preparingStepDownload") },
  { key: "converting", label: t("pages.audioToSubtitle.preparingStepConvert") },
]);

const activeIndex = computed(() => Math.max(0, steps.value.findIndex((step) => step.key === props.phase)));

const hints = computed(() => [
  t("pages.audioToSubtitle.preparingHint1"),
  t("pages.audioToSubtitle.preparingHint2"),
  t("pages.audioToSubtitle.preparingHint3"),
]);

const now = ref(Date.now());
const hintIndex = ref(0);
let tickTimer: ReturnType<typeof setInterval> | null = null;
let hintTimer: ReturnType<typeof setInterval> | null = null;

const elapsedMs = computed(() => Math.max(0, now.value - (props.startedAt || now.value)));
const isLongWait = computed(() => elapsedMs.value >= LONG_WAIT_MS);

// Empty until mounted so the first step is a content change, not part of the
// region's initial render (which screen readers often skip).
const isMounted = ref(false);
const liveText = computed(() => {
  if (!isMounted.value) return "";
  const step = steps.value[activeIndex.value];
  const parts = [`${t("pages.audioToSubtitle.preparingTitle")}: ${step?.label ?? ""}`];
  if (isLongWait.value) parts.push(t("pages.audioToSubtitle.preparingLongWait"));
  return parts.join(". ");
});

const elapsedLabel = computed(() => {
  const totalSeconds = Math.floor(elapsedMs.value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

onMounted(() => {
  isMounted.value = true;
  now.value = Date.now();
  tickTimer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
  hintTimer = setInterval(() => {
    hintIndex.value = (hintIndex.value + 1) % hints.value.length;
  }, HINT_ROTATE_MS);
});

onBeforeUnmount(() => {
  if (tickTimer) clearInterval(tickTimer);
  if (hintTimer) clearInterval(hintTimer);
});
</script>
